// Utilidades para validación jerárquica de roles

export type RoleName = 'admin_global' | 'user_global' | 'admin_nacional' | 'user_nacional' | 'admin_diocesano' | 'user_diocesano' | 'admin_local' | 'user_local';

export interface RoleHierarchy {
  canRequest: RoleName[];
  canApprove: RoleName[];
}

// Mapeo de nombres de roles de la BD a nombres internos
export function normalizeRoleName(roleName: string): RoleName | null {
  const normalized = roleName.toLowerCase().replace(/\s+/g, '_');

  if (normalized === 'admin_global') return 'admin_global';
  if (normalized === 'user_global') return 'user_global';
  if (normalized === 'admin_nacional') return 'admin_nacional';
  if (normalized === 'user_nacional') return 'user_nacional';
  if (normalized === 'admin_diocesano') return 'admin_diocesano';
  if (normalized === 'user_diocesano') return 'user_diocesano';
  if (normalized === 'admin_local') return 'admin_local';
  if (normalized === 'user_local') return 'user_local';

  return null;
}

// Mapeo inverso: de nombre interno a nombre de display
export function getRoleDisplayName(roleName: RoleName): string {
  const displayNames: Record<RoleName, string> = {
    admin_global: 'Admin Global',
    user_global: 'User Global',
    admin_nacional: 'Admin Nacional',
    user_nacional: 'User Nacional',
    admin_diocesano: 'Admin Diocesano',
    user_diocesano: 'User Diocesano',
    admin_local: 'Admin Local',
    user_local: 'User Local',
  };

  return displayNames[roleName] || roleName;
}

// Definición de jerarquía: qué roles puede solicitar y aprobar cada nivel
const hierarchyRules: Record<RoleName, RoleHierarchy> = {
  admin_global: {
    canRequest: ['admin_global', 'user_global', 'admin_nacional', 'user_nacional', 'admin_diocesano', 'user_diocesano', 'admin_local', 'user_local'],
    canApprove: ['admin_global', 'user_global', 'admin_nacional', 'user_nacional', 'admin_diocesano', 'user_diocesano', 'admin_local', 'user_local'],
  },
  user_global: {
    canRequest: [],
    canApprove: [],
  },
  admin_nacional: {
    canRequest: ['admin_nacional', 'user_nacional', 'admin_diocesano', 'user_diocesano', 'admin_local', 'user_local'],
    canApprove: ['admin_diocesano', 'user_diocesano', 'admin_local', 'user_local'],
  },
  user_nacional: {
    canRequest: [],
    canApprove: [],
  },
  admin_diocesano: {
    canRequest: ['admin_diocesano', 'user_diocesano', 'admin_local', 'user_local'],
    canApprove: ['admin_local', 'user_local'],
  },
  user_diocesano: {
    canRequest: [],
    canApprove: [],
  },
  admin_local: {
    canRequest: ['admin_local', 'user_local'],
    canApprove: [],
  },
  user_local: {
    canRequest: [],
    canApprove: [],
  },
};

/**
 * Obtiene los roles que un usuario puede solicitar según su rol actual
 */
export function getRolesCanRequest(currentRole: string): RoleName[] {
  const normalized = normalizeRoleName(currentRole);
  if (!normalized) return [];

  return hierarchyRules[normalized]?.canRequest || [];
}

/**
 * Obtiene los roles que un usuario puede aprobar según su rol actual
 */
export function getRolesCanApprove(currentRole: string): RoleName[] {
  const normalized = normalizeRoleName(currentRole);
  if (!normalized) return [];

  return hierarchyRules[normalized]?.canApprove || [];
}

/**
 * Verifica si un usuario puede solicitar un rol específico
 */
export function canRequestRole(currentRole: string, requestedRole: string): boolean {
  const canRequest = getRolesCanRequest(currentRole);
  const normalizedRequested = normalizeRoleName(requestedRole);

  if (!normalizedRequested) return false;

  return canRequest.includes(normalizedRequested);
}

/**
 * Verifica si un usuario puede aprobar una solicitud de un rol específico
 */
export function canApproveRole(approverRole: string, requestedRole: string): boolean {
  const canApprove = getRolesCanApprove(approverRole);
  const normalizedRequested = normalizeRoleName(requestedRole);

  if (!normalizedRequested) return false;

  return canApprove.includes(normalizedRequested);
}

/**
 * Verifica si un usuario es admin global (puede auto-aprobar)
 */
export function isAdminGlobal(roleName: string): boolean {
  const normalized = normalizeRoleName(roleName);
  return normalized === 'admin_global';
}

/**
 * Determina el nivel jerárquico de un rol (MENOR número = más autoridad)
 * Los niveles coinciden con la tabla roles de la base de datos
 */
export function getRoleLevel(roleName: string): number {
  const normalized = normalizeRoleName(roleName);

  const levels: Record<RoleName, number> = {
    admin_global: 1,
    user_global: 1,
    admin_nacional: 2,
    user_nacional: 2,
    admin_diocesano: 3,
    user_diocesano: 3,
    admin_local: 4,
    user_local: 4,
  };

  return normalized ? levels[normalized] : 999;
}

/**
 * Obtiene el ámbito geográfico requerido para un rol
 */
export function getRoleScope(roleName: RoleName): 'global' | 'provincia' | 'diocesis' | 'lumisial' {
  if (roleName === 'admin_global' || roleName === 'user_global') {
    return 'global';
  }
  if (roleName === 'admin_nacional' || roleName === 'user_nacional') {
    return 'provincia';
  }
  if (roleName === 'admin_diocesano' || roleName === 'user_diocesano') {
    return 'diocesis';
  }
  return 'lumisial';
}

/**
 * Verifica si un usuario puede modificar (desactivar/remover) a otro usuario
 * Un admin solo puede modificar usuarios de nivel inferior (número mayor)
 * MENOR nivel = MÁS autoridad
 */
export function canModifyUser(adminRole: string, targetRole: string): boolean {
  const adminLevel = getRoleLevel(adminRole);
  const targetLevel = getRoleLevel(targetRole);

  return adminLevel < targetLevel;
}
